import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Camera, ChevronRight, X } from 'lucide-react';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { CustomerPicker } from '../components/customer-picker.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Pill } from '../components/ui/pill.js';
import { Feedback } from '../components/ui/feedback.js';
import {
  api,
  type CustomerSummary,
  type InventoryItemSummary,
} from '../lib/api.js';
import { countPending, getOutboxDb } from '../lib/outbox.js';
import {
  computeQuoteTotals,
  useQuoteDraft,
  type QuoteDraftLine,
} from '../lib/quote-draft.js';
import {
  addQuoteDraftPhoto,
  clearQuoteDraftPhotos,
  enqueueSalesOrder,
  generateEntryId,
  listQuoteDraftPhotos,
  removeQuoteDraftPhoto,
  type SalesOrderOutboxEntry,
} from '../lib/outbox.js';
import { blobToBase64, downscaleImage } from '../lib/image-upload.js';
import { drainOutbox } from '../lib/outbox-processor.js';
import { SignaturePadComponent } from '../components/signature-pad.js';

// ZXing is ~400KB — load on demand so it doesn't bloat the initial PWA bundle.
const ScanModal = lazy(() =>
  import('../components/scan-modal.js').then((m) => ({ default: m.ScanModal })),
);

interface QuoteBuilderSearch {
  customerNo?: string;
  addWhse?: string;
  addPartNo?: string;
}

export function QuoteBuilderScreen() {
  const queryClient = useQueryClient();
  const draft = useQuoteDraft();
  const totals = computeQuoteTotals(draft.lines);
  const search = useSearch({ from: '/quote-builder' }) as QuoteBuilderSearch;
  const deepLinkNavigate = useNavigate({ from: '/quote-builder' });

  // Deep-link from inventory detail: pre-fill customer and append a line.
  // Resolves description/uom from the item if we can fetch it, then clears
  // the URL params so a re-render doesn't add the line again.
  useEffect(() => {
    if (!search.customerNo && !search.addPartNo) return;
    let cancelled = false;
    (async () => {
      if (search.customerNo && !draft.customerNo) {
        draft.setCustomerNo(search.customerNo);
      }
      if (search.addPartNo && search.addWhse) {
        let description = '';
        let uom = 'EA';
        try {
          const matches = await api.inventory.search({
            whse: search.addWhse,
            partNo: search.addPartNo,
            limit: 1,
          });
          const hit = matches.find((m) => m.partNo.trim() === search.addPartNo);
          if (hit) {
            description = hit.description ?? '';
            uom = hit.uom ?? 'EA';
          }
        } catch {
          // Network error — proceed with bare part info; user can edit before submit.
        }
        if (cancelled) return;
        draft.addLine({
          whse: search.addWhse,
          partNo: search.addPartNo,
          description,
          orderQty: '1',
          unitPrice: null,
          sellMeasure: uom,
        });
      }
      if (!cancelled) {
        void deepLinkNavigate({ search: {}, replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally one-shot: read URL params on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customerLookup = useQuery({
    queryKey: ['customers', 'lookup', draft.customerNo],
    queryFn: ({ signal }) =>
      // Exact match — the old substring lookup (limit 5) reported "Not
      // found" whenever the number was contained in >5 other customers'.
      api.customers.search({ customerNoExact: draft.customerNo, limit: 5 }, signal),
    enabled: draft.customerNo.length > 0,
    staleTime: 60_000,
  });
  const resolvedCustomer: CustomerSummary | undefined = customerLookup.data?.find(
    (c) => c.customerNo === draft.customerNo,
  );

  // Warehouse default chain: customer's default → the rep's own default →
  // '00' (Spire's conventional main warehouse — the previous hardcoded
  // 'MAIN' doesn't exist on most installs and produced "Spire couldn't
  // find PART in MAIN" for every line; audit quick-win).
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => api.me.get(signal),
    staleTime: 5 * 60_000,
  });
  const defaultWhse =
    resolvedCustomer?.defaultWarehouse ?? meQuery.data?.defaultWarehouse ?? '00';

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">New quote</h1>

      <CustomerChipCard
        customerNo={draft.customerNo}
        resolved={resolvedCustomer}
        looking={customerLookup.isPending && draft.customerNo.length > 0}
        onChange={(no) => draft.setCustomerNo(no)}
        onPick={(c) => {
          // The picker already has the full record — seed the lookup so the
          // chip appears instantly instead of after a second Spire call.
          queryClient.setQueryData(['customers', 'lookup', c.customerNo], [c]);
          draft.setCustomerNo(c.customerNo);
        }}
      />

      <Card>
        <CardHeader className="flex items-center justify-between gap-2 pb-2">
          <div className="text-xs uppercase tracking-wider text-ink-subtle">
            Lines ({draft.lines.length})
          </div>
        </CardHeader>
        <CardBody className="space-y-3">
          <AddLineForm
            defaultWhse={defaultWhse}
            customerNo={draft.customerNo}
            onAdd={draft.addLine}
          />

          {draft.lines.length === 0 ? (
            <div className="text-sm text-ink-muted">No lines yet.</div>
          ) : (
            <ul className="divide-y" style={{ borderColor: 'var(--color-border-tertiary)' }}>
              {draft.lines.map((line) => (
                <DraftLineRow
                  key={line.clientId}
                  line={line}
                  onChange={(patch) => draft.updateLine(line.clientId, patch)}
                  onRemove={() => draft.removeLine(line.clientId)}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Totals</CardTitle>
        </CardHeader>
        <CardBody className="text-sm space-y-1.5">
          <Row label="Priced lines" value={String(totals.pricedLineCount)} />
          {totals.unpricedLineCount > 0 && (
            <Row
              label="Unpriced lines"
              value={`${totals.unpricedLineCount} (Spire will compute pricing)`}
            />
          )}
          <div className="pt-2 mt-2 border-t border-line-subtle">
            <Row label="Subtotal (priced only)" value={`$${totals.subtotal}`} bold />
          </div>
          <p className="text-xs text-ink-subtle pt-2">
            Final taxes and totals are computed server-side by Spire when the order is created.
          </p>
        </CardBody>
      </Card>

      <PhotosAndNotesCard />

      <SignatureCard />

      <SubmitBar />

      <SyncStatusFooter linesCount={draft.lines.length} />
    </div>
  );
}

/**
 * Customer chip card — mockup-style. When the entered customerNo resolves to
 * a Spire customer, we show a compact card with an avatar disc, the name and
 * a "address · terms · level" subtitle, and a chevron that opens the editor.
 * When unresolved, we show the input directly.
 */
function CustomerChipCard({
  customerNo,
  resolved,
  looking,
  onChange,
  onPick,
}: {
  customerNo: string;
  resolved: CustomerSummary | undefined;
  looking: boolean;
  onChange: (no: string) => void;
  onPick: (customer: CustomerSummary) => void;
}) {
  const [editing, setEditing] = useState(!resolved);

  // Auto-collapse to chip mode as soon as a resolved customer arrives.
  useEffect(() => {
    if (resolved) setEditing(false);
  }, [resolved]);

  if (editing || !resolved) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="text-xs uppercase tracking-wider text-ink-subtle">Customer</div>
        </CardHeader>
        <CardBody className="space-y-2">
          <CustomerPicker
            value={customerNo}
            onSelect={onPick}
            onClear={() => onChange('')}
            autoFocus
          />
          {customerNo && (
            <div className="text-sm">
              {looking ? (
                <span className="text-ink-muted">Looking up…</span>
              ) : (
                <span className="text-amber-700">
                  Customer {customerNo} wasn't found in Spire — search again by name or number.
                </span>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    );
  }

  const subtitleParts = [
    resolved.address.line1,
    `Level ${resolved.sellLevel}`,
    resolved.defaultWarehouse ? `Whse ${resolved.defaultWarehouse}` : null,
  ].filter(Boolean);

  return (
    <Card className="cursor-pointer" onClick={() => setEditing(true)}>
      <CardBody className="flex items-center gap-3">
        <CustomerAvatar name={resolved.name} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="font-medium text-ink truncate">{resolved.name}</div>
            {resolved.hold && <Pill variant="danger">on hold</Pill>}
          </div>
          <div className="mt-0.5 text-xs text-ink-muted truncate">
            {subtitleParts.join(' · ')}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden />
      </CardBody>
    </Card>
  );
}

function CustomerAvatar({ name }: { name: string }) {
  const initials =
    name
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  return (
    <span
      aria-hidden
      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
      style={{ backgroundColor: 'var(--pill-service-bg)', color: 'var(--pill-service-fg)' }}
    >
      {initials}
    </span>
  );
}

function SyncStatusFooter({ linesCount }: { linesCount: number }) {
  const pending = useLiveQuery(() => countPending(getOutboxDb()), [], 0);
  const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
  return (
    <div className="flex items-center justify-center gap-3 text-xs text-ink-muted pt-2">
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: online ? 'var(--trade-pm)' : 'var(--trade-sales)' }}
        />
        Draft · {online ? 'online' : 'offline'} · {linesCount} line{linesCount === 1 ? '' : 's'}
      </span>
      {pending > 0 && (
        <Link to="/outbox" className="hover:underline">
          {pending} pending sync
        </Link>
      )}
    </div>
  );
}

/** Most photos a single quote carries — keeps the offline outbox entry a sane size. */
const MAX_QUOTE_PHOTOS = 10;
/** Just under AddPhotoInputDto's @MaxLength(7_500_000) on `base64`. */
const MAX_PHOTO_BASE64_CHARS = 7_000_000;

/**
 * Photos + a written note for the office / installers. Both ride the offline
 * outbox: after the quote is created in Spire the note becomes a 'Tinsmith
 * quote note' CRM note and the photos land in the job's files (where the
 * installer sees them) and the order's CRM notes.
 */
function PhotosAndNotesCard() {
  const draft = useQuoteDraft();
  const photos = useLiveQuery(() => listQuoteDraftPhotos(), [], []);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const room = MAX_QUOTE_PHOTOS - photos.length;

  const onPicked = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length === 0) return;
    setError(null);
    setBusy(true);
    try {
      if (picked.length > room) {
        setError(`Only ${MAX_QUOTE_PHOTOS} photos per quote — added the first ${Math.max(room, 0)}.`);
      }
      for (const file of picked.slice(0, Math.max(room, 0))) {
        // Always re-encode to JPEG when we can: smaller in IndexedDB and on
        // the wire, and it normalizes HEIC-ish camera output where supported.
        const shrunk = await downscaleImage(file);
        const isPng = file.type === 'image/png';
        const blob = shrunk ?? file;
        if (!shrunk && file.type !== 'image/jpeg' && !isPng) {
          setError(`${file.name}: only JPEG or PNG photos can be attached to a quote.`);
          continue;
        }
        const base64 = await blobToBase64(blob);
        // The photo endpoint caps the encoded payload (~5 MB of image). A
        // photo over that would fail permanently in the outbox, long after
        // the rep has left the site — refuse it now instead.
        if (base64.length > MAX_PHOTO_BASE64_CHARS) {
          setError(`${file.name} is too large to attach (max about 5 MB).`);
          continue;
        }
        await addQuoteDraftPhoto({
          contentType: shrunk || !isPng ? 'image/jpeg' : 'image/png',
          base64,
          filename: shrunk ? file.name.replace(/\.[^.]+$/, '') + '.jpg' : file.name,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="text-xs uppercase tracking-wider text-ink-subtle">Photos &amp; notes</div>
      </CardHeader>
      <CardBody className="space-y-3">
        <div className="space-y-1">
          <label className="text-xs text-ink-muted" htmlFor="quote-note">
            Notes for the office and installers
          </label>
          <textarea
            id="quote-note"
            value={draft.note}
            onChange={(e) => draft.setNote(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Site access, measurements, what the customer asked for…"
            className="block w-full rounded-lg border bg-surface px-3 py-2 text-base text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand"
            style={{ borderColor: 'var(--color-border-secondary)' }}
          />
        </div>

        {photos.length > 0 && (
          <ul className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {photos.map((p) => (
              <li key={p.clientId} className="relative">
                <img
                  src={`data:${p.contentType};base64,${p.base64}`}
                  alt={p.filename}
                  className="aspect-square w-full rounded-lg object-cover bg-surface-muted"
                />
                <button
                  type="button"
                  onClick={() => void removeQuoteDraftPhoto(p.clientId)}
                  aria-label={`Remove ${p.filename}`}
                  className="absolute right-1 top-1 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png"
          onChange={(e) => void onPicked(e)}
          className="sr-only"
          tabIndex={-1}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={busy || room <= 0}
          onClick={() => fileInputRef.current?.click()}
        >
          <Camera className="h-4 w-4" aria-hidden />
          {busy ? 'Adding…' : photos.length > 0 ? 'Add more photos' : 'Add photos'}
        </Button>
        {error && <Feedback variant="warning" size="sm">{error}</Feedback>}
        <p className="text-xs text-ink-subtle">
          Optional. Saved with the quote — they upload once it reaches Spire, even if you're
          offline now, and show on the job for the installer.
        </p>
      </CardBody>
    </Card>
  );
}

function SignatureCard() {
  const draft = useQuoteDraft();
  const [expanded, setExpanded] = useState(false);

  const hasSignature = draft.signature !== null;

  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-3">
        <CardTitle>Customer signature</CardTitle>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-sm text-brand hover:underline"
        >
          {expanded ? 'Hide' : hasSignature ? 'Replace' : 'Add'}
        </button>
      </CardHeader>
      {(expanded || !hasSignature) && expanded && (
        <CardBody>
          <SignaturePadComponent
            initial={hasSignature ? `data:image/png;base64,${draft.signature!.base64}` : null}
            onCapture={(sig) => {
              draft.setSignature(sig);
              setExpanded(false);
            }}
            onClear={() => draft.setSignature(null)}
          />
        </CardBody>
      )}
      {!expanded && hasSignature && (
        <CardBody className="flex items-center gap-3">
          <img
            alt="Captured signature"
            src={`data:image/png;base64,${draft.signature!.base64}`}
            className="h-12 max-w-[160px] object-contain border border-line-subtle rounded bg-surface"
          />
          <span className="text-xs text-ink-muted">
            Signature captured. Will upload to Spire when the order is created.
          </span>
        </CardBody>
      )}
      {!expanded && !hasSignature && (
        <CardBody>
          <p className="text-xs text-ink-subtle">
            Optional — capture a customer signature on the device. It uploads as a CRM
            attachment on the new quote.
          </p>
        </CardBody>
      )}
    </Card>
  );
}

interface AddLineFormProps {
  defaultWhse: string;
  customerNo: string;
  onAdd: (line: Omit<QuoteDraftLine, 'clientId'>) => void;
}

function AddLineForm({ defaultWhse, customerNo, onAdd }: AddLineFormProps) {
  const [whse, setWhse] = useState(defaultWhse);
  // Re-sync when the default resolves AFTER mount (customer lookup is
  // async) — but never stomp a warehouse the rep typed themselves.
  const lastDefaultRef = useRef(defaultWhse);
  useEffect(() => {
    if (defaultWhse !== lastDefaultRef.current) {
      setWhse((current) => (current === lastDefaultRef.current ? defaultWhse : current));
      lastDefaultRef.current = defaultWhse;
    }
  }, [defaultWhse]);
  const [partNo, setPartNo] = useState('');
  const [orderQty, setOrderQty] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');
  const [description, setDescription] = useState('');
  const [sellMeasure, setSellMeasure] = useState('EA');
  const qtyRef = useRef<HTMLInputElement>(null);
  const [suggestPicked, setSuggestPicked] = useState(false);

  // Live-pricing hint — debounced so we don't hammer the API as the user types.
  const [debouncedTrigger, setDebouncedTrigger] = useState({ partNo: '', qty: '' });
  useEffect(() => {
    const id = setTimeout(() => setDebouncedTrigger({ partNo: partNo.trim(), qty: orderQty }), 350);
    return () => clearTimeout(id);
  }, [partNo, orderQty]);

  const priceLookup = useQuery({
    queryKey: ['quote-builder', 'price', customerNo, whse, debouncedTrigger.partNo, debouncedTrigger.qty],
    queryFn: ({ signal }) =>
      api.pricing.resolve(
        {
          customerNo,
          items: [
            {
              whse,
              partNo: debouncedTrigger.partNo,
              orderQty: debouncedTrigger.qty || '1',
            },
          ],
        },
        signal,
      ),
    enabled:
      customerNo.length >= 2 &&
      whse.length >= 1 &&
      debouncedTrigger.partNo.length >= 2,
    staleTime: 30_000,
    retry: false,
  });
  const hintLine = priceLookup.data?.lines[0] ?? null;
  const showHint = hintLine?.found && unitPrice === '';

  const handlePartNoChange = (next: string) => {
    setPartNo(next);
    setSuggestPicked(false);
  };

  const onSuggestionPick = (item: InventoryItemSummary) => {
    setPartNo(item.partNo);
    if (item.description) setDescription(item.description);
    if (item.uom) setSellMeasure(item.uom);
    setSuggestPicked(true);
    // Defer focus to next tick so the dropdown unmounts cleanly.
    setTimeout(() => qtyRef.current?.focus(), 0);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!partNo.trim() || !orderQty.trim()) return;
    onAdd({
      whse: whse.trim().toUpperCase(),
      partNo: partNo.trim(),
      description: description.trim(),
      orderQty: orderQty.trim(),
      unitPrice: unitPrice.trim() === '' ? null : unitPrice.trim(),
      sellMeasure: sellMeasure.trim() || 'EA',
    });
    setPartNo('');
    setOrderQty('1');
    setUnitPrice('');
    setDescription('');
    setSellMeasure('EA');
    setSuggestPicked(false);
  };

  const [scanOpen, setScanOpen] = useState(false);

  return (
    <form onSubmit={submit} className="space-y-2">
      <div className="grid grid-cols-[80px_1fr_auto] gap-2">
        <Input
          aria-label="Warehouse"
          value={whse}
          onChange={(e) => setWhse(e.target.value)}
          className="font-mono"
          autoCapitalize="characters"
        />
        <PartNoCombobox
          value={partNo}
          onChange={handlePartNoChange}
          onPick={onSuggestionPick}
          whse={whse}
          suppressed={suggestPicked}
        />
        <button
          type="button"
          onClick={() => setScanOpen(true)}
          aria-label="Scan barcode"
          title="Scan barcode"
          className="h-11 w-11 rounded-lg border border-line-soft bg-surface hover:bg-surface-muted active:bg-surface-subtle flex items-center justify-center text-ink"
        >
          <ScanIcon />
        </button>
      </div>
      {scanOpen && (
        <Suspense fallback={null}>
          <ScanModal
            open={scanOpen}
            onClose={() => setScanOpen(false)}
            onScan={({ text }) => {
              setScanOpen(false);
              // Treat the scanned code as the canonical partNo. Trim + uppercase
              // to match how the rest of the app normalizes Spire codes.
              handlePartNoChange(text.trim().toUpperCase());
            }}
          />
        </Suspense>
      )}
      {/* Phone: description takes its own row so qty / price / Add fit the card. */}
      <div className="grid grid-cols-[1fr_1fr_auto] sm:grid-cols-[1fr_120px_120px_auto] gap-2">
        <Input
          aria-label="Description (optional)"
          placeholder="Description (optional)"
          className="col-span-3 sm:col-span-1"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Input
          ref={qtyRef}
          aria-label="Quantity"
          type="number"
          step="any"
          min="0"
          placeholder="Qty"
          className="min-w-0"
          value={orderQty}
          onChange={(e) => setOrderQty(e.target.value)}
        />
        <Input
          aria-label="Unit price (optional)"
          type="number"
          step="any"
          min="0"
          placeholder="Unit price"
          className="min-w-0"
          value={unitPrice}
          onChange={(e) => setUnitPrice(e.target.value)}
        />
        <Button type="submit" size="md">
          Add
        </Button>
      </div>
      {showHint && hintLine && (
        <div className="flex items-center justify-between gap-3 text-xs px-1">
          <span className="text-ink-muted">
            Spire price: <span className="font-mono font-semibold">${hintLine.unitPrice}</span>{' '}
            <span className="text-ink-subtle">({describeSource(hintLine)})</span>
          </span>
          <button
            type="button"
            className="text-brand hover:underline"
            onClick={() => setUnitPrice(hintLine.unitPrice)}
          >
            Use ${hintLine.unitPrice}
          </button>
        </div>
      )}
      {priceLookup.data && hintLine && !hintLine.found && (
        <div className="text-xs text-amber-700 px-1">
          Spire couldn't find <span className="font-mono">{hintLine.partNo}</span> in {whse}.
        </div>
      )}
    </form>
  );
}

interface DraftLineRowProps {
  line: QuoteDraftLine;
  onChange: (patch: Partial<QuoteDraftLine>) => void;
  onRemove: () => void;
}

function DraftLineRow({ line, onChange, onRemove }: DraftLineRowProps) {
  const qty = Number(line.orderQty);
  const price = line.unitPrice !== null ? Number(line.unitPrice) : null;
  const extended =
    price !== null && Number.isFinite(qty) && Number.isFinite(price)
      ? (qty * price).toFixed(2)
      : null;
  return (
    <li className="py-3">
      <div className="grid grid-cols-[1fr_auto] gap-2 items-start">
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink truncate">
            {line.description || line.partNo}
          </div>
          {line.description && (
            <div className="text-[11px] font-mono text-ink-subtle truncate">{line.partNo}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          {extended !== null ? (
            <div className="text-sm font-medium text-ink tabular-nums">${extended}</div>
          ) : (
            <Pill variant="neutral">Spire prices</Pill>
          )}
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${line.partNo}`}
            className="mt-0.5 text-[11px] text-ink-subtle hover:text-red-700"
          >
            Remove
          </button>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[11px] text-ink-muted">
        <span className="font-mono">whse {line.whse}</span>
        <label className="inline-flex items-center gap-1">
          qty
          <Input
            aria-label="Quantity"
            type="number"
            step="any"
            min="0"
            value={line.orderQty}
            onChange={(e) => onChange({ orderQty: e.target.value })}
            className="h-7 w-16 text-xs"
          />
        </label>
        <label className="inline-flex items-center gap-1">
          price
          <Input
            aria-label="Unit price"
            type="number"
            step="any"
            min="0"
            value={line.unitPrice ?? ''}
            placeholder="auto"
            onChange={(e) => onChange({ unitPrice: e.target.value === '' ? null : e.target.value })}
            className="h-7 w-20 text-xs"
          />
        </label>
      </div>
    </li>
  );
}

function SubmitBar() {
  const draft = useQuoteDraft();
  const navigate = useNavigate();
  // Track the queued ENTRY ID and live-query the row so the card updates
  // in place when the background drain finishes — pre-fix it rendered a
  // frozen enqueue-time snapshot and a rep never saw the Spire order
  // number their submit produced (audit P0-12).
  const [queuedId, setQueuedId] = useState<string | null>(null);
  const queued = useLiveQuery(
    async () => (queuedId ? await getOutboxDb().salesOrderOutbox.get(queuedId) : undefined),
    [queuedId],
  );

  const mutation = useMutation<SalesOrderOutboxEntry, Error, 'W' | 'Q'>({
    mutationFn: async (type) => {
      const entry = await enqueueSalesOrder({
        entryId: generateEntryId(),
        idempotencyKey: draft.idempotencyKey,
        body: {
          customerNo: draft.customerNo,
          type,
          hold: draft.hold,
          orderDate: draft.orderDate || undefined,
          requiredDate: draft.requiredDate || undefined,
          customerPO: draft.customerPO || undefined,
          items: draft.lines.map((line) => ({
            whse: line.whse,
            partNo: line.partNo,
            description: line.description || undefined,
            orderQty: line.orderQty,
            unitPrice: line.unitPrice ?? undefined,
            sellMeasure: line.sellMeasure || undefined,
          })),
        },
        signature: draft.signature,
        photos: (await listQuoteDraftPhotos()).map(({ contentType, base64, filename }) => ({
          contentType,
          base64,
          filename,
        })),
        note: draft.note,
      });
      // The photos now live on the outbox entry — free the draft's copies.
      await clearQuoteDraftPhotos();
      // Try to drain immediately so the common (online) case feels instant.
      // Failures are handled by the background processor; we don't surface
      // them here — the queued card links to /outbox where the user can see.
      void drainOutbox();
      return entry;
    },
    onSuccess: (entry) => {
      setQueuedId(entry.entryId);
      draft.reset();
    },
  });

  const canSubmit =
    draft.customerNo.length > 0 &&
    draft.lines.length > 0 &&
    draft.lines.every((l) => Number(l.orderQty) > 0 && l.partNo.length > 0);

  if (queuedId) {
    const sent = queued?.status === 'sent';
    const failed = queued?.status === 'failed';
    return (
      <Card className="border-brand-200 bg-brand-50">
        <CardBody className="space-y-1">
          <div className="text-sm font-semibold text-brand-700">
            {sent ? 'Submitted to Spire.' : failed ? 'Submit hit a problem.' : 'Queued for sync.'}
          </div>
          {sent && queued?.lastOrderNo && queued.lastSpireId !== null ? (
            <p className="text-xs text-ink">
              Created as{' '}
              <Link
                to="/sales-orders/$id"
                params={{ id: String(queued.lastSpireId) }}
                className="font-mono text-ink-accent hover:underline"
              >
                {queued.lastOrderNo}
              </Link>
              {queued.body.type === 'Q' ? ' (quote)' : ' (order)'}.
            </p>
          ) : failed ? (
            <p className="text-xs text-ink">
              {queued?.lastError ?? 'Submission failed.'} Manage it from the outbox.
            </p>
          ) : (
            <p className="text-xs text-ink">
              Your quote is saved on this device and will submit to Spire as soon as the
              connection is available.
            </p>
          )}
          <div className="flex gap-3 items-baseline pt-2">
            <Link to="/outbox" className="text-sm text-brand hover:underline">
              View outbox →
            </Link>
            <button
              type="button"
              className="text-sm text-ink-muted hover:underline"
              onClick={() => {
                setQueuedId(null);
                void navigate({ to: '/quote-builder' });
              }}
            >
              Start another
            </button>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {mutation.isError && (
        <Feedback variant="error" size="lg">
          <div className="font-semibold">Couldn't save locally.</div>
          <div className="text-xs mt-1 opacity-80">{mutation.error?.message}</div>
        </Feedback>
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => mutation.mutate('Q')}
          disabled={!canSubmit || mutation.isPending}
          className="flex-1"
        >
          {mutation.isPending && mutation.variables === 'Q' ? 'Submitting…' : 'Save as quote'}
        </Button>
        <Button
          type="button"
          onClick={() => mutation.mutate('W')}
          disabled={!canSubmit || mutation.isPending}
          className="flex-1"
        >
          {mutation.isPending && mutation.variables === 'W' ? 'Submitting…' : 'Create order'}
        </Button>
      </div>
    </div>
  );
}

function ScanIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <path d="M7 8v8" />
      <path d="M11 8v8" />
      <path d="M15 8v8" />
      <path d="M19 8v8" />
    </svg>
  );
}

function describeSource(line: { source: 'matrix' | 'customer_sell_level' | 'item_default'; sellLevel: number | null }): string {
  switch (line.source) {
    case 'matrix':
      return 'matrix rule';
    case 'customer_sell_level':
      return `customer level ${line.sellLevel ?? '?'}`;
    case 'item_default':
      return 'item default';
  }
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between gap-3 ${bold ? 'text-base font-semibold text-ink' : ''}`}>
      <span className={bold ? '' : 'text-ink-subtle'}>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

interface PartNoComboboxProps {
  value: string;
  whse: string;
  onChange: (next: string) => void;
  onPick: (item: InventoryItemSummary) => void;
  /** True when the user just picked a suggestion — suppresses re-opening the dropdown. */
  suppressed: boolean;
}

/**
 * Part-number input with a live inventory dropdown. Debounced 250ms, hits
 * the existing /api/v1/inventory?q=... endpoint. Click or Enter selects;
 * Esc / blur dismisses.
 */
function PartNoCombobox({ value, whse, onChange, onPick, suppressed }: PartNoComboboxProps) {
  const [debounced, setDebounced] = useState(value);
  const [highlight, setHighlight] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), 250);
    return () => clearTimeout(id);
  }, [value]);

  const enabled = !suppressed && Boolean(whse) && debounced.trim().length >= 2;

  const query = useQuery({
    queryKey: ['quote-builder', 'inventory-suggest', whse, debounced],
    queryFn: ({ signal }) =>
      api.inventory.search({ whse, q: debounced.trim(), limit: 6 }, signal),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const matches = enabled ? query.data ?? [] : [];
  const showDropdown = open && enabled && matches.length > 0;

  // Keep highlight inside bounds when the suggestion list changes.
  useEffect(() => {
    if (highlight >= matches.length) setHighlight(0);
  }, [matches.length, highlight]);

  const choose = (item: InventoryItemSummary) => {
    setOpen(false);
    onPick(item);
  };

  return (
    <div className="relative">
      <Input
        aria-label="Part number"
        placeholder="Part # or description (type to search)"
        value={value}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay so the click on a suggestion gets through.
          setTimeout(() => setOpen(false), 120);
        }}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (!showDropdown) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => (h + 1) % matches.length);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => (h - 1 + matches.length) % matches.length);
          } else if (e.key === 'Enter') {
            const item = matches[highlight];
            if (item) {
              e.preventDefault();
              choose(item);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className="font-mono"
        autoCapitalize="characters"
        autoCorrect="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
      />
      {showDropdown && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full mt-1 z-10 bg-surface border rounded-lg shadow-lg overflow-hidden"
          style={{ borderColor: 'var(--color-border-tertiary)' }}
        >
          {matches.map((item, idx) => (
            <li key={item.spireId}>
              <button
                type="button"
                role="option"
                aria-selected={idx === highlight}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-3 ${
                  idx === highlight ? 'bg-brand-50' : 'hover:bg-surface-muted'
                }`}
                onMouseDown={(e) => {
                  // Prevent the input's blur from firing first.
                  e.preventDefault();
                }}
                onClick={() => choose(item)}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-mono font-semibold text-ink truncate">
                    {item.partNo}
                  </div>
                  <div className="text-xs text-ink-muted truncate">
                    {item.description ?? '—'}
                  </div>
                </div>
                <div className="text-right text-xs text-ink-subtle shrink-0">
                  <div>{item.availableQty} {item.uom ?? ''}</div>
                  <div>${item.defaultSellPrice}</div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
